import type { MetadataItem } from '../tool-config-types';

type PythonOutput = {
  success?: boolean;
  error?: string;
  output?: unknown;
  result?: unknown;
  script?: string;
  code?: string;
};

type MetadataExtractorContext = {
  input: unknown;
  output?: unknown;
  status?: string;
};

const isPythonOutput = (output: unknown): output is PythonOutput =>
  typeof output === 'object' && output !== null;

const isRunningStatus = (status?: string): boolean =>
  status === 'started' || status === 'in-progress';

const isErrorStatus = (status?: string): boolean => status === 'error';

const hasValue = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return true;
};

const hasContent = (output: PythonOutput): boolean =>
  hasValue(output.script) ||
  hasValue(output.code) ||
  hasValue(output.output) ||
  hasValue(output.result);

const hasResult = (output: PythonOutput): boolean =>
  hasValue(output.result) || hasValue(output.output);

export const extractPythonMetadata = ({
  output,
  status,
}: MetadataExtractorContext): MetadataItem[] => {
  const pythonOutput = isPythonOutput(output) ? output : null;

  // Check error status first (from tool status or output.error)
  if (isErrorStatus(status) || pythonOutput?.error) {
    return [{ label: 'Status', value: 'Error' }];
  }

  if (isRunningStatus(status)) {
    if (!pythonOutput || !hasContent(pythonOutput)) {
      return [{ label: 'Status', value: '● Generating...' }];
    }
    if (
      (hasValue(pythonOutput.script) || hasValue(pythonOutput.code)) &&
      !hasResult(pythonOutput)
    ) {
      return [{ label: 'Status', value: '● Executing...' }];
    }
  }

  if (!pythonOutput) return [];

  if (pythonOutput.success === true) {
    return [{ label: 'Status', value: 'Success' }];
  }
  if (hasResult(pythonOutput)) {
    return [{ label: 'Status', value: 'Complete' }];
  }

  return [];
};
